import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Box, Chip, Collapse, CircularProgress,
  Skeleton, Typography, Button, Switch, FormControlLabel, Tooltip, Select, MenuItem,
  TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";

/* utils */
const n = (v) => (v === "" || v == null ? null : Number(v));
const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");

/* Dialog genérico de subtipo (usado nos 3 níveis) */
const SubDialog = ({ open, title, initial = {}, onClose, onSave }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    id: initial?.id ?? undefined,
    nome: initial?.nome ?? "",
    ordem: initial?.ordem ?? "",
    status: initial?.status == null ? 1 : Number(initial.status),
    // novo: default
    default: initial?.default == null ? 0 : Number(initial.default),
  });

  React.useEffect(() => {
    setForm({
      id: initial?.id ?? undefined,
      nome: initial?.nome ?? "",
      ordem: initial?.ordem ?? "",
      status: initial?.status == null ? 1 : Number(initial.status),
      default: initial?.default == null ? 0 : Number(initial.default),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.id]);

  const canSave = form.nome.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Editar ${title}` : `Novo ${title}`}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 2 }}>
          <TextField
            label="Nome *"
            value={form.nome}
            onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
          />
          <TextField
            label="Ordem"
            value={form.ordem ?? ""}
            inputMode="numeric"
            onChange={(e) => setForm((p) => ({ ...p, ordem: digitsOnly(e.target.value) }))}
          />
        </Box>

        <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch
                checked={Number(form.status) === 1}
                onChange={(e) =>
                  setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))
                }
              />
            }
            label={Number(form.status) === 1 ? "Ativo" : "Inativo"}
          />
          {/* novo: switch default */}
          <FormControlLabel
            control={
              <Switch
                checked={Number(form.default) === 1}
                onChange={(e) =>
                  setForm((p) => ({ ...p, default: e.target.checked ? 1 : 0 }))
                }
              />
            }
            label={Number(form.default) === 1 ? "Default" : "Não default"}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() =>
            onSave({
              id: form.id,
              nome: form.nome.trim(),
              ordem: n(form.ordem),
              status: Number(form.status) === 1 ? 1 : 0,
              default: Number(form.default) === 1 ? 1 : 0, // <- enviado
            })
          }
          disabled={!canSave}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TypeTable = ({
  /* dados */
  types = [],
  rowCount = 0,
  page = 0,       // zero-based
  pageSize = 50,
  isLoading = false,
  /* paginação / busca */
  onPageChange,
  onPageSizeChange,
  onSearch,
  /* ações nível 0 (tipo) */
  onEditType,
  onEditSubtype,
  onDeleteType,
  onToggleTypeStatus,
  /* ações nível 1 */
  onUpsertSubtype1,
  onDeleteSubtype1,
  onToggleSubtype1Status,
  /* ações nível 2 */
  onUpsertSubtype2,
  onDeleteSubtype2,
  onToggleSubtype2Status,
  /* ações nível 3 */
  onUpsertSubtype3,
  onDeleteSubtype3,
  onToggleSubtype3Status,
}) => {
  const { t: tSettings } = useTranslation("settings");
  const [localSearch, setLocalSearch] = useState("");

  /* expansões */
  const [expType, setExpType] = useState(new Set()); // tipos
  const [expL1, setExpL1] = useState(new Set());     // `${typeId}:${sub1Id}`
  const [expL2, setExpL2] = useState(new Set());     // `${typeId}:${sub1Id}:${sub2Id}`

  const toggleType = (id) => {
    setExpType((prev) => {
      const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    });
  };
  const keyL1 = (typeId, sub1Id) => `${typeId}:${sub1Id}`;
  const keyL2 = (typeId, sub1Id, sub2Id) => `${typeId}:${sub1Id}:${sub2Id}`;

  const toggleL1 = (typeId, sub1Id) => {
    const k = keyL1(typeId, sub1Id);
    setExpL1((prev) => {
      const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next;
    });
  };

  const toggleL2 = (typeId, sub1Id, sub2Id) => {
    const k = keyL2(typeId, sub1Id, sub2Id);
    setExpL2((prev) => {
      const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next;
    });
  };

  /* dialog genérico */
  const [dlg, setDlg] = useState({
    open: false,
    level: 1, // 1 | 2 | 3
    title: "Subtipo 1",
    typeId: null,
    sub1Id: null,
    sub2Id: null,
    initial: null,
  });

  const openDlg = (level, title, ctx) =>
    setDlg({ open: true, level, title, ...ctx });

  const closeDlg = () =>
    setDlg({ open: false, level: 1, title: "Subtipo 1", typeId: null, sub1Id: null, sub2Id: null, initial: null });

  const handleSaveDlg = (sub) => {
    if (dlg.level === 1) {
      onUpsertSubtype1?.({ typeId: dlg.typeId, sub });
    } else if (dlg.level === 2) {
      onUpsertSubtype2?.({ typeId: dlg.typeId, sub1Id: dlg.sub1Id, sub });
    } else {
      onUpsertSubtype3?.({ typeId: dlg.typeId, sub1Id: dlg.sub1Id, sub2Id: dlg.sub2Id, sub });
    }
    closeDlg();
  };

  const ordered = useMemo(
    () => types.slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    [types]
  );

  return (
    <Paper variant="outlined">
      {/* busca */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder={tSettings("shared.searchPlaceholder")}
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48} />
              <TableCell>{tSettings("typeTable.colOrder")}</TableCell>
              <TableCell>{tSettings("shared.name")}</TableCell>
              <TableCell>{tSettings("shared.status")}</TableCell>
              <TableCell>Subtipo1</TableCell>
              <TableCell>{tSettings("typeTable.colRolesPerm")}</TableCell>
              <TableCell align="right">{tSettings("shared.actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <TableRow key={row}>
                    <TableCell><Skeleton variant="text" width={32} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width="80%" height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={48} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={64} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
                    <TableCell align="right"><Skeleton variant="rounded" width={80} height={28} sx={{ ml: "auto" }} /></TableCell>
                  </TableRow>
                ))}
              </>
            ) : ordered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {tSettings("typeTable.noTypes")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              ordered.map((t) => {
                const isOpenType = expType.has(t.id);
                const s1 = (t.children || []).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

                return (
                  <React.Fragment key={t.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleType(t.id)}>
                          {isOpenType ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{t.ordem ?? ""}</TableCell>
                      <TableCell>{t.nome}{" "}
                        {!!t.default && <Chip size="small" label="default" sx={{ ml: 1 }} />}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Switch
                            size="small"
                            checked={!!t.status}
                            onChange={() => onToggleTypeStatus?.(t)}
                          />
                          <Chip
                            size="small"
                            label={t.status ? "Ativo" : "Inativo"}
                            color={t.status ? "success" : "default"}
                            variant="outlined"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={`${s1.length} item(ns)`} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={`${(t.allowed_positions || []).length} cargo(s)`} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Adicionar Subtipo 1">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openDlg(1, "Subtipo 1", { typeId: t.id, sub1Id: null, sub2Id: null, initial: null })
                            }
                          >
                            <PlaylistAddRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => onEditType?.(t)}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => onDeleteType?.(t.id)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Nível 1 */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpenType} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
                            {s1.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                Nenhum Subtipo 1.
                              </Typography>
                            ) : (
                              <Table size="small" sx={{ background: "transparent" }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell width={48} />
                                    <TableCell>Ordem</TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Subtipo2</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {s1.map((s) => {
                                    const k1 = keyL1(t.id, s.id);
                                    const open1 = expL1.has(k1);
                                    const s2 = (s.children || []).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
                                    return (
                                      <React.Fragment key={s.id}>
                                        <TableRow>
                                          <TableCell>
                                            <IconButton size="small" onClick={() => toggleL1(t.id, s.id)}>
                                              {open1 ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                                            </IconButton>
                                          </TableCell>
                                          <TableCell>{s.ordem ?? ""}</TableCell>
                                          <TableCell>
                                            {s.nome}{" "}
                                            {!!s.default && <Chip size="small" label="default" sx={{ ml: 1 }} />}
                                          </TableCell>
                                          <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                              <Switch
                                                size="small"
                                                checked={!!s.status}
                                                onChange={() =>
                                                  onToggleSubtype1Status?.({ typeId: t.id, sub: s })
                                                }
                                              />
                                              <Chip
                                                size="small"
                                                label={s.status ? "Ativo" : "Inativo"}
                                                color={s.status ? "success" : "default"}
                                                variant="outlined"
                                              />
                                            </Stack>
                                          </TableCell>
                                          <TableCell>
                                            <Chip size="small" label={`${s2.length} item(ns)`} />
                                          </TableCell>
                                          <TableCell align="right">
                                            <Tooltip title="Adicionar Subtipo 2">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  openDlg(2, "Subtipo 2", {
                                                    typeId: t.id, sub1Id: s.id, sub2Id: null, initial: null
                                                  })
                                                }
                                              >
                                                <PlaylistAddRoundedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  onEditSubtype?.({
                                                    level: 1,
                                                    sub: s,
                                                    typeId: t.id,
                                                  })
                                                }
                                              >
                                                <EditRoundedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  onDeleteSubtype1?.({ typeId: t.id, sub1Id: s.id })
                                                }
                                              >
                                                <DeleteOutlineRoundedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>

                                        {/* Nível 2 */}
                                        <TableRow>
                                          <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                                            <Collapse in={open1} timeout="auto" unmountOnExit>
                                              <Box sx={{ px: 2, py: 1.5, bgcolor: "action.selected" }}>
                                                {s2.length === 0 ? (
                                                  <Typography variant="body2" color="text.secondary">
                                                    Nenhum Subtipo 2.
                                                  </Typography>
                                                ) : (
                                                  <Table size="small" sx={{ background: "transparent" }}>
                                                    <TableHead>
                                                      <TableRow>
                                                        <TableCell width={48} />
                                                        <TableCell>Ordem</TableCell>
                                                        <TableCell>Nome</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell>Subtipo3</TableCell>
                                                        <TableCell align="right">Ações</TableCell>
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {s2.map((u) => {
                                                        const k2 = keyL2(t.id, s.id, u.id);
                                                        const open2 = expL2.has(k2);
                                                        const s3 = (u.children || []).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
                                                        return (
                                                          <React.Fragment key={u.id}>
                                                            <TableRow>
                                                              <TableCell>
                                                                <IconButton size="small" onClick={() => toggleL2(t.id, s.id, u.id)}>
                                                                  {open2 ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                                                                </IconButton>
                                                              </TableCell>
                                                              <TableCell>{u.ordem ?? ""}</TableCell>
                                                              <TableCell>
                                                                {u.nome}{" "}
                                                                {!!u.default && <Chip size="small" label="default" sx={{ ml: 1 }} />}
                                                              </TableCell>
                                                              <TableCell>
                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                  <Switch
                                                                    size="small"
                                                                    checked={!!u.status}
                                                                    onChange={() =>
                                                                      onToggleSubtype2Status?.({ typeId: t.id, sub1Id: s.id, sub: u })
                                                                    }
                                                                  />
                                                                  <Chip
                                                                    size="small"
                                                                    label={u.status ? "Ativo" : "Inativo"}
                                                                    color={u.status ? "success" : "default"}
                                                                    variant="outlined"
                                                                  />
                                                                </Stack>
                                                              </TableCell>
                                                              <TableCell>
                                                                <Chip size="small" label={`${s3.length} item(ns)`} />
                                                              </TableCell>
                                                              <TableCell align="right">
                                                                <Tooltip title="Adicionar Subtipo 3">
                                                                  <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                      openDlg(3, "Subtipo 3", {
                                                                        typeId: t.id, sub1Id: s.id, sub2Id: u.id, initial: null
                                                                      })
                                                                    }
                                                                  >
                                                                    <PlaylistAddRoundedIcon fontSize="small" />
                                                                  </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Editar">
                                                                  <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                      onEditSubtype?.({
                                                                        level: 2,
                                                                        sub: u,
                                                                        typeId: t.id,
                                                                        sub1Id: s.id,
                                                                      })
                                                                    }
                                                                  >
                                                                    <EditRoundedIcon fontSize="small" />
                                                                  </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Excluir">
                                                                  <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                      onDeleteSubtype2?.({ typeId: t.id, sub1Id: s.id, sub2Id: u.id })
                                                                    }
                                                                  >
                                                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                                                  </IconButton>
                                                                </Tooltip>
                                                              </TableCell>
                                                            </TableRow>

                                                            {/* Nível 3 */}
                                                            <TableRow>
                                                              <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                                                                <Collapse in={open2} timeout="auto" unmountOnExit>
                                                                  <Box sx={{ px: 2, py: 1.5, bgcolor: "action.focus" }}>
                                                                    {s3.length === 0 ? (
                                                                      <Typography variant="body2" color="text.secondary">
                                                                        Nenhum Subtipo 3.
                                                                      </Typography>
                                                                    ) : (
                                                                      <Table size="small" sx={{ background: "transparent" }}>
                                                                        <TableHead>
                                                                          <TableRow>
                                                                            <TableCell>Ordem</TableCell>
                                                                            <TableCell>Nome</TableCell>
                                                                            <TableCell>Status</TableCell>
                                                                            <TableCell align="right">Ações</TableCell>
                                                                          </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                          {s3.map((v) => (
                                                                            <TableRow key={v.id}>
                                                                              <TableCell>{v.ordem ?? ""}</TableCell>
                                                                              <TableCell>
                                                                                {v.nome}{" "}
                                                                                {!!v.default && <Chip size="small" label="default" sx={{ ml: 1 }} />}
                                                                              </TableCell>
                                                                              <TableCell>
                                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                                  <Switch
                                                                                    size="small"
                                                                                    checked={!!v.status}
                                                                                    onChange={() =>
                                                                                      onToggleSubtype3Status?.({
                                                                                        typeId: t.id, sub1Id: s.id, sub2Id: u.id, sub: v
                                                                                      })
                                                                                    }
                                                                                  />
                                                                                  <Chip
                                                                                    size="small"
                                                                                    label={v.status ? "Ativo" : "Inativo"}
                                                                                    color={v.status ? "success" : "default"}
                                                                                    variant="outlined"
                                                                                  />
                                                                                </Stack>
                                                                              </TableCell>
                                                                              <TableCell align="right">
                                                                                <Tooltip title="Editar">
                                                                                  <IconButton
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                      onEditSubtype?.({
                                                                                        level: 3,
                                                                                        sub: v,
                                                                                        typeId: t.id,
                                                                                        sub1Id: s.id,
                                                                                        sub2Id: u.id,
                                                                                      })
                                                                                    }
                                                                                  >
                                                                                    <EditRoundedIcon fontSize="small" />
                                                                                  </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Excluir">
                                                                                  <IconButton
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                      onDeleteSubtype3?.({
                                                                                        typeId: t.id, sub1Id: s.id, sub2Id: u.id, sub3Id: v.id
                                                                                      })
                                                                                    }
                                                                                  >
                                                                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                                                                  </IconButton>
                                                                                </Tooltip>
                                                                              </TableCell>
                                                                            </TableRow>
                                                                          ))}
                                                                        </TableBody>
                                                                      </Table>
                                                                    )}
                                                                  </Box>
                                                                </Collapse>
                                                              </TableCell>
                                                            </TableRow>
                                                          </React.Fragment>
                                                        );
                                                      })}
                                                    </TableBody>
                                                  </Table>
                                                )}
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* paginação */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Linhas por página:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((sz) => (
              <MenuItem key={sz} value={sz}>{sz}</MenuItem>
            ))}
          </Select>
        </Box>

        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>

      {/* dialog genérico */}
      <SubDialog
        open={dlg.open}
        title={dlg.title}
        initial={dlg.initial}
        onClose={closeDlg}
        onSave={handleSaveDlg}
      />
    </Paper>
  );
};

export default TypeTable;
